import { gql } from '@apollo/client'
import { Heading } from '@farol-ds/react'
import { useMetrics } from '@jusbrasil-web/docview/core'
import { useEffect, useMemo } from 'react'

import { LawsuitSubjects_DocumentFragment } from './__generated__/lawsuit-subjects.graphql'
import { CollapsedSubjects, SubjectsList } from './components'
import { MAX_VISIBLE_SUBJECTS, PRIMARY_SUBJECTS } from './constants'
import styles from './lawsuit-subjects.module.scss'
import { SubjectProps } from './types'

export type LawsuitSubjectsProps = {
  document: LawsuitSubjects_DocumentFragment
}

export function LawsuitSubjects(props: LawsuitSubjectsProps) {
  const { document } = props
  const { ref: dispatcherRef, sendMetrics } = useMetrics<HTMLDivElement>()

  const { lawsuit } = document
  const mainSubject = lawsuit?.subjects?.[0]

  const subjects = useMemo(() => {
    const siblings = lawsuit?.mainSubject?.siblings || []
    return mainSubject ? [{ name: mainSubject }, ...siblings] : siblings
  }, [lawsuit?.mainSubject?.siblings, mainSubject])

  const primarySubjects = useMemo(() => {
    return subjects.filter((item) =>
      PRIMARY_SUBJECTS.includes(item?.name as (typeof PRIMARY_SUBJECTS)[number])
    )
  }, [subjects])

  const shouldRenderLawsuitSubjects =
    mainSubject && subjects.length > 1 && primarySubjects.length < 2

  useEffect(() => {
    if (!shouldRenderLawsuitSubjects) return

    let eventTriggered = false
    const observer = new IntersectionObserver(
      (entries) => {
        if (!eventTriggered && entries[0]?.isIntersecting) {
          eventTriggered = true

          sendMetrics('Peca.LawsuitSubjectsViewed')

          observer.disconnect()
        }
      },
      { threshold: 0.25 }
    )
    observer.observe(dispatcherRef.current as HTMLDivElement)

    return () => {
      observer.disconnect()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!shouldRenderLawsuitSubjects) {
    return null
  }

  return (
    <section ref={dispatcherRef}>
      <Heading className={styles.title} size="xl" asChild>
        <h2>Pesquisas relacionadas</h2>
      </Heading>

      {subjects.length > MAX_VISIBLE_SUBJECTS ? (
        <CollapsedSubjects subjects={subjects as SubjectProps[]} />
      ) : (
        <SubjectsList subjects={subjects as SubjectProps[]} />
      )}
    </section>
  )
}

LawsuitSubjects.fragments = {
  document: gql`
    fragment LawsuitSubjects_document on Document {
      lawsuit {
        mainSubject {
          parent {
            name
          }
          parents {
            name
          }
          siblings {
            name
          }
        }
        subjects
      }
    }
  `,
}
